import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { NumericInput } from "@/components/ui/numeric-input";
import { MoneyInput } from "@/components/ui/money-input";
import { formatErrorMessage } from "@/lib/errorMessages";
import { createAirportService, updateAirportService } from "@/lib/airportServicesApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const formSchema = z.object({
  service_code: z.string().min(1, "Service code is required"),
  airport: z.string().min(1, "Airport is required"),
  service_name: z.string().min(1, "Service name is required"),
  description: z.string().optional(),
  service_type: z.enum(["arrival", "departure", "transit"]),
  pricing_type: z.enum(["per_person", "per_family", "per_group"]),
  price_op: z.string().min(1, "Price OP is required").refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price OP must be greater than or equal to 0"),
  price_ag: z.string().min(1, "Price AG is required").refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price AG must be greater than or equal to 0"),
  price_cf: z.string().min(1, "Price CF is required").refine((val) => {
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Price CF must be greater than or equal to 0"),
  supplier_name: z.string().optional(),
  cost_supplier: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Cost Supplier must be greater than or equal to 0"),
  tax_percentage: z.string().optional().refine((val) => {
    if (!val || val === "") return true;
    const num = parseFloat(val);
    return !isNaN(num) && num >= 0;
  }, "Tax percentage must be greater than or equal to 0"),
  hcte_notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface AirportServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: any;
  onSuccess: () => void;
}

const AirportServiceForm = ({
  open,
  onOpenChange,
  initialData,
  onSuccess,
}: AirportServiceFormProps) => {
  const { toast } = useToast();
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      service_code: "",
      airport: "",
      service_name: "",
      description: "",
      service_type: "arrival",
      pricing_type: "per_person",
      price_op: "",
      price_ag: "",
      price_cf: "",
      supplier_name: "",
      cost_supplier: "",
      tax_percentage: "",
      hcte_notes: "",
    },
  });

  const {
    handleOpenChange: handleUnsavedOpenChange,
    ConfirmDialog,
    pendingClose,
    setPendingClose,
  } = useUnsavedChanges({
    isDirty: form.formState.isDirty,
    onSave: () => form.handleSubmit(onSubmit)(),
    onDiscard: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (pendingClose) {
      onOpenChange(false);
      setPendingClose(false);
    }
  }, [pendingClose, onOpenChange, setPendingClose]);

  useEffect(() => {
    if (initialData) {
      form.reset({
        service_code: initialData.service_code || "",
        airport: initialData.airport || "",
        service_name: initialData.service_name || "",
        description: initialData.description || "",
        service_type: initialData.service_type || "arrival",
        pricing_type: initialData.pricing_type || "per_person",
        price_op: initialData.price_op?.toString() || "",
        price_ag: initialData.price_ag?.toString() || "",
        price_cf: initialData.price_cf?.toString() || "",
        supplier_name: initialData.supplier_name || "",
        cost_supplier: initialData.cost_supplier?.toString() || "",
        tax_percentage: initialData.tax_percentage?.toString() || "",
        hcte_notes: initialData.hcte_notes || "",
      });
    } else {
      form.reset({
        service_code: "",
        airport: "",
        service_name: "",
        description: "",
        service_type: "arrival",
        pricing_type: "per_person",
        price_op: "",
        price_ag: "",
        price_cf: "",
        supplier_name: "",
        cost_supplier: "",
        tax_percentage: "",
        hcte_notes: "",
      });
    }
  }, [initialData, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        service_code: data.service_code,
        airport: data.airport,
        service_name: data.service_name,
        description: data.description || null,
        service_type: data.service_type,
        pricing_type: data.pricing_type,
        price_op: parseFloat(data.price_op),
        price_ag: parseFloat(data.price_ag),
        price_cf: parseFloat(data.price_cf),
        supplier_name: data.supplier_name || null,
        cost_supplier: data.cost_supplier ? parseFloat(data.cost_supplier) : null,
        tax_percentage: data.tax_percentage ? parseFloat(data.tax_percentage) : null,
        hcte_notes: data.hcte_notes || null,
      };

      if (initialData?.id) {
        await updateAirportService(initialData.id, payload);
        toast({ title: "Success", description: "Service updated successfully" });
      } else {
        await createAirportService(payload);
        toast({ title: "Success", description: "Service created successfully" });
      }

      form.reset();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: formatErrorMessage(error, "Failed to save service"),
        variant: "destructive",
      });
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      const shouldClose = handleUnsavedOpenChange(open);
      if (shouldClose) {
        onOpenChange(false);
      }
    } else {
      onOpenChange(open);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Edit Airport Service" : "Add New Airport Service"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="service_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service Code *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="airport"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Airport *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="service_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="service_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Service Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="arrival">Arrival</SelectItem>
                        <SelectItem value="departure">Departure</SelectItem>
                        <SelectItem value="transit">Transit</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pricing_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pricing Type *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="per_person">Per person</SelectItem>
                        <SelectItem value="per_family">Per family</SelectItem>
                        <SelectItem value="per_group">Per group</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="price_op"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price OP (USD) *</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_ag"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price AG (USD) *</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="price_cf"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price CF (USD) *</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cost_supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost Supplier (USD)</FormLabel>
                    <FormControl>
                      <MoneyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value || "")}
                        onBlur={field.onBlur}
                        className="w-[140px]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="tax_percentage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tax (%)</FormLabel>
                  <FormControl>
                    <NumericInput
                      value={field.value}
                      onChange={(value) => field.onChange(value || "")}
                      onBlur={field.onBlur}
                      allowDecimals={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hcte_notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HCTE Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={4} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">Save</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    <ConfirmDialog />
    </>
  );
};

export default AirportServiceForm;
